import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatDate, formatDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Store, Plus, ShoppingBag, TrendingUp, TrendingDown, Trash2, Receipt, Wallet, Calculator,
  ChevronLeft, ChevronRight, XCircle, RotateCcw, CheckCircle2, AlertTriangle, Clock,
  Pencil, Banknote, Search, Scale, ArrowRight, Phone, MapPin, RefreshCw, Eye,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import NewSaleDialog from "@/pages/NewSaleDialog";
import { istToday } from "@/lib/time";
import {
  CancelSaleDialog,
  CollectPaymentDialog,
  PaymentHistoryDialog,
  SalesSummary,
  PendingDeletionsDialog,
  PendingReturnApprovalsDialog,
  SaleDetailDialog,
  SelectShopForSaleDialog,
} from "@/pages/sales";
import { DrawerReconciliationTab } from "@/pages/sales/DrawerReconciliationTab";
import SalesReturnDialog from "@/pages/sales/SalesReturnDialog";

const PAY_MODES = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
  { value: "credit", label: "Credit" },
];

export default function Sales() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState("");
  const [tab, setTab] = useState("sales");
  // Top-level date range — defaults to today→today (IST). Drives Sales / Expenses / Incomes / Credits tabs.
  const [dateFrom, setDateFrom] = useState(() => istToday());
  const [dateTo, setDateTo] = useState(() => istToday());

  const ALL_SHOPS = "__all__";
  const isAllShops = shopId === ALL_SHOPS;

  const loadShops = async () => {
    try {
      const r = await api.get("/shops");
      const items = r.data.items || [];
      setShops(items);
      return items;
    } catch (e) {
      toast.error(formatApiError(e));
      return [];
    }
  };

  // Save the selected shop as user's default (persists across sessions)
  const saveDefaultShop = async (newShopId) => {
    if (!newShopId || newShopId === ALL_SHOPS) return;
    try {
      await api.post("/users/me/default-shop", { shop_id: newShopId });
    } catch (e) {
      // Silent fail - don't interrupt UX for preference save
      console.warn("Failed to save default shop:", e);
    }
  };

  // Handle shop selection change
  const handleShopChange = (newShopId) => {
    setShopId(newShopId);
    // Save as default (non-blocking)
    if (newShopId && newShopId !== ALL_SHOPS) {
      saveDefaultShop(newShopId);
    }
  };

  useEffect(() => {
    loadShops().then((items) => {
      if (items.length) {
        // Priority: user's default_shop_id > first shop in list
        const defaultShop = user?.default_shop_id && items.find((s) => s.id === user.default_shop_id);
        if (defaultShop) {
          setShopId(defaultShop.id);
        } else {
          // Fallback to first shop and save it as default
          setShopId(items[0].id);
          saveDefaultShop(items[0].id);
        }
      }
    });
  }, [user?.default_shop_id]);

  const currentShop = useMemo(
    () => (isAllShops ? null : shops.find((s) => s.id === shopId)),
    [shops, shopId, isAllShops],
  );

  // Compose the filter passed to all child tabs.
  const filter = useMemo(
    () => ({
      shop: currentShop,
      shopId: isAllShops ? null : shopId,
      isAllShops,
      dateFrom,
      dateTo,
    }),
    [currentShop, shopId, isAllShops, dateFrom, dateTo],
  );

  const setRangeToday = () => {
    const t = istToday();
    setDateFrom(t);
    setDateTo(t);
  };
  const setRangeYesterday = () => {
    const t = istToday();
    const [y, m, d] = t.split("-").map(Number);
    const a = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    a.setUTCDate(a.getUTCDate() - 1);
    const yISO = a.toISOString().slice(0, 10);
    setDateFrom(yISO);
    setDateTo(yISO);
  };
  const setRangeThisMonth = () => {
    const t = istToday();
    const [y, m] = t.split("-");
    setDateFrom(`${y}-${m}-01`);
    setDateTo(t);
  };

  // Cross-shop summary removed per latest design — Sales page no longer shows
  // lifetime totals on top. The filtered SalesTab summary already covers it.

  return (
    <div className="fade-up" data-testid="sales-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Daily ops</p>
        <h1 className="font-heading text-3xl tracking-tight">Sales</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Record daily sales, expenses and incomes.
        </p>
      </div>

      {/* Shop selector */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="shop-selector">
        <span className="text-xs uppercase tracking-widest text-muted-foreground mr-2 w-full sm:w-auto">
          <Store className="w-3 h-3 inline-block mr-1" /> Shop
        </span>
        {(user?.role === "master" || user?.role === "super_user") && (
          <button
            type="button"
            onClick={() => handleShopChange(ALL_SHOPS)}
            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-colors border ${
              isAllShops
                ? "bg-[#9E6D5B] text-white border-[#9E6D5B]"
                : "bg-white text-foreground border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
            }`}
            data-testid="shop-tab-all"
          >
            All shops
          </button>
        )}
        {shops.map((s) => (
          <button
            key={s.id}
            onClick={() => handleShopChange(s.id)}
            disabled={user?.role !== "master" && user?.role !== "super_user" && Array.isArray(user?.shop_ids) && user.shop_ids.length > 0 && !user.shop_ids.includes(s.id)}
            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-colors border ${
              shopId === s.id
                ? "bg-[#9E6D5B] text-white border-[#9E6D5B]"
                : "bg-white text-foreground border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
            data-testid={`shop-tab-${s.slug || s.name.toLowerCase()}`}
          >
            {s.name}
          </button>
        ))}
        {!shops.length && (
          <span className="text-sm text-muted-foreground">No shops configured.</span>
        )}
      </div>

      {/* Date range */}
      <div className="flex flex-wrap items-end gap-2 mb-4" data-testid="date-range">
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">From</Label>
          <Input
            type="date"
            value={dateFrom}
            max={istToday()}
            onChange={(e) => setDateFrom(e.target.value)}
            className="mt-1 w-[140px] sm:w-[160px] bg-white"
            data-testid="filter-date-from"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">To</Label>
          <Input
            type="date"
            value={dateTo}
            max={istToday()}
            min={dateFrom}
            onChange={(e) => setDateTo(e.target.value)}
            className="mt-1 w-[140px] sm:w-[160px] bg-white"
            data-testid="filter-date-to"
          />
        </div>
        <div className="flex flex-wrap gap-1.5 sm:ml-1">
          <Button size="sm" variant="outline" onClick={setRangeToday} data-testid="filter-range-today">Today</Button>
          <Button size="sm" variant="outline" onClick={setRangeYesterday} data-testid="filter-range-yesterday">Yesterday</Button>
          <Button size="sm" variant="outline" onClick={setRangeThisMonth} data-testid="filter-range-month">This month</Button>
        </div>
      </div>

      {!shopId ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          Pick a shop above to continue.
        </Card>
      ) : (
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <div className="overflow-x-auto -mx-1 px-1 pb-1">
            <TabsList data-testid="sales-tabs" className="inline-flex w-max">
              <TabsTrigger value="sales" data-testid="tab-sales">
                <ShoppingBag className="w-4 h-4 mr-2" /> Sales
              </TabsTrigger>
              <TabsTrigger value="expenses" data-testid="tab-expenses">
                <TrendingDown className="w-4 h-4 mr-2" /> Expenses
              </TabsTrigger>
              <TabsTrigger value="incomes" data-testid="tab-incomes">
                <TrendingUp className="w-4 h-4 mr-2" /> Income
              </TabsTrigger>
              <TabsTrigger
                value="cash"
                data-testid="tab-cash"
                disabled={isAllShops}
                title={isAllShops ? "Select a specific shop to open cash register" : undefined}
              >
                <Wallet className="w-4 h-4 mr-2" /> <span className="hidden sm:inline">Opening/Closing</span><span className="sm:hidden">Cash</span>
              </TabsTrigger>
              <TabsTrigger
                value="drawer"
                data-testid="tab-drawer"
                disabled={isAllShops}
                title={isAllShops ? "Select a specific shop to view drawer reconciliation" : undefined}
              >
                <Calculator className="w-4 h-4 mr-2" /> Drawer
              </TabsTrigger>
              <TabsTrigger value="balance" data-testid="tab-balance">
                <Scale className="w-4 h-4 mr-2" /> Balance
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="sales" className="mt-4">
            <SalesTab filter={filter} />
          </TabsContent>
          <TabsContent value="expenses" className="mt-4">
            <LedgerTab
              filter={filter}
              kind="expenses"
              title="Expenses"
              endpoint="/shop-expenses"
              tone="rose"
            />
          </TabsContent>
          <TabsContent value="incomes" className="mt-4">
            <LedgerTab
              filter={filter}
              kind="incomes"
              title="Other income"
              endpoint="/shop-incomes"
              tone="emerald"
            />
          </TabsContent>
          <TabsContent value="cash" className="mt-4">
            {isAllShops ? (
              <Card className="p-10 text-center text-sm text-muted-foreground">
                Cash register is per-shop. Pick a specific shop above to manage opening/closing cash.
              </Card>
            ) : (
              <CashRegisterTab shop={currentShop} filter={filter} />
            )}
          </TabsContent>
          <TabsContent value="drawer" className="mt-4">
            <DrawerReconciliationTab filter={filter} currentShop={currentShop} />
          </TabsContent>
          <TabsContent value="balance" className="mt-4">
            <BalanceTab />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function SalesTab({ filter }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { shop, shopId, isAllShops, dateFrom, dateTo } = filter;
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [shopPaymentSummary, setShopPaymentSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openSaleTabs, setOpenSaleTabs] = useState([]); // Array of {id, name, shop}
  const [nextSaleTabId, setNextSaleTabId] = useState(1);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [returnTarget, setReturnTarget] = useState(null);
  const [viewTarget, setViewTarget] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all"); // all | paid | partial | unpaid
  const [paymentModeFilter, setPaymentModeFilter] = useState("all"); // all | cash | upi | card | credit
  const [pendingDeletions, setPendingDeletions] = useState([]);
  const [pendingDeletionsOpen, setPendingDeletionsOpen] = useState(false);
  const [pendingReturnApprovals, setPendingReturnApprovals] = useState([]);
  const [pendingReturnApprovalsOpen, setPendingReturnApprovalsOpen] = useState(false);
  const [shopSelectOpen, setShopSelectOpen] = useState(false);
  const [accessibleShops, setAccessibleShops] = useState([]);
  
  const MAX_SALE_TABS = 5;

  // Load accessible shops for the user
  useEffect(() => {
    const loadAccessibleShops = async () => {
      try {
        const { data } = await api.get("/shops");
        let shops = Array.isArray(data) ? data : (data?.items || []);
        
        // Filter to user's accessible shops (unless master/super_user)
        if (user?.role !== "master" && user?.role !== "super_user" && Array.isArray(user?.shop_ids) && user.shop_ids.length > 0) {
          shops = shops.filter(s => user.shop_ids.includes(s.id));
        }
        
        setAccessibleShops(shops);
      } catch (e) {
        console.error("Failed to load shops:", e);
      }
    };
    loadAccessibleShops();
  }, [user]);
  
  const openNewSaleTab = (selectedShop = null) => {
    if (openSaleTabs.length >= MAX_SALE_TABS) {
      toast.error(`Maximum ${MAX_SALE_TABS} concurrent sales allowed`);
      return;
    }
    
    // Determine which shop to use
    const shopForSale = selectedShop || shop;
    
    if (!shopForSale) {
      toast.error("Please select a shop first");
      return;
    }
    
    const newTab = { 
      id: nextSaleTabId, 
      name: `SALE ${nextSaleTabId}`,
      shop: shopForSale 
    };
    setOpenSaleTabs([...openSaleTabs, newTab]);
    setNextSaleTabId(nextSaleTabId + 1);
  };

  const handleNewSaleClick = () => {
    // Always show shop selection if user has access to multiple shops
    if (accessibleShops.length === 0) {
      toast.error("No shops available");
      return;
    }
    
    if (accessibleShops.length === 1) {
      // Only one shop - open directly without dialog
      openNewSaleTab(accessibleShops[0]);
    } else {
      // Multiple shops - always show selection dialog to prevent mistakes
      setShopSelectOpen(true);
    }
  };
  
  const closeSaleTab = (tabId) => {
    setOpenSaleTabs(openSaleTabs.filter((t) => t.id !== tabId));
  };

  const loadPendingDeletions = async () => {
    try {
      const { data } = await api.get("/sales/pending-deletions");
      setPendingDeletions(data.items || []);
    } catch (e) {
      // Silently fail - not critical
    }
  };

  const loadPendingReturnApprovals = async () => {
    if (user?.role !== "master" && user?.role !== "super_user") return;
    try {
      const { data } = await api.get("/sales-returns/pending-approvals");
      setPendingReturnApprovals(data.items || []);
    } catch (e) {
      // Silently fail - not critical
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const range = { date_from: dateFrom, date_to: dateTo };
      const params = { ...range };
      if (shopId) params.shop_id = shopId;
      const listParams = { ...params, limit: 500 };
      if (statusFilter !== "all") listParams.payment_status = statusFilter;
      if (paymentModeFilter !== "all") listParams.payment_mode = paymentModeFilter;
      const [list, sum, shopPay] = await Promise.all([
        api.get("/sales", { params: listParams }),
        api.get("/sales/summary", { params }),
        api.get("/credit/shop-payment-summary", { params }),
      ]);
      setItems(list.data.items || []);
      setSummary(sum.data);
      setShopPaymentSummary(shopPay.data?.shops || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadPendingDeletions();
    loadPendingReturnApprovals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, dateFrom, dateTo, isAllShops, statusFilter, paymentModeFilter]);

  const rangeLabel = () => {
    if (dateFrom === dateTo) {
      const t = istToday();
      if (dateFrom === t) return "Today";
      const [y, m, d] = dateFrom.split("-").map(Number);
      const anchor = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
      return anchor.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
    }
    return `${dateFrom} → ${dateTo}`;
  };

  return (
    <div>
      <SalesSummary summary={summary} shopPaymentSummary={shopPaymentSummary} />

      <div className="flex items-center gap-2 sm:gap-3 mb-4 flex-wrap" data-testid="sales-date-nav">
        <span className="text-xs sm:text-sm font-medium text-muted-foreground w-full sm:w-auto" data-testid="sales-date-label">
          {isAllShops ? "All shops · " : ""}{rangeLabel()}
        </span>

        <div className="flex items-center gap-1.5 flex-wrap" data-testid="status-filter-pills">
          {[
            { key: "all", label: "All" },
            { key: "paid", label: "Paid" },
            { key: "partial", label: "Partial" },
            { key: "unpaid", label: "Unpaid" },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setStatusFilter(opt.key)}
              className={`px-2.5 sm:px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                statusFilter === opt.key
                  ? "bg-[#9E6D5B] text-white border-[#9E6D5B]"
                  : "bg-white text-muted-foreground border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
              }`}
              data-testid={`status-filter-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Payment Mode Filter */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs text-muted-foreground">PAY:</span>
          {[
            { key: "all", label: "All" },
            { key: "cash", label: "Cash" },
            { key: "upi", label: "UPI" },
            { key: "card", label: "Card" },
            { key: "credit", label: "Credit" },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setPaymentModeFilter(opt.key)}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors border ${
                paymentModeFilter === opt.key
                  ? "bg-[#9E6D5B] text-white border-[#9E6D5B]"
                  : "bg-white text-muted-foreground border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Pending Deletions Badge — master/super_user only; click to open review dialog */}
        {pendingDeletions.length > 0 && (user?.role === "master" || user?.role === "super_user") && (
          <button
            type="button"
            onClick={() => setPendingDeletionsOpen(true)}
            className="flex items-center gap-2 px-3 py-1 rounded-md bg-orange-50 border border-orange-200 hover:bg-orange-100 hover:border-orange-300 transition-colors cursor-pointer"
            data-testid="pending-deletions-badge"
            title="Click to review staff-deleted sales"
          >
            <AlertTriangle className="w-4 h-4 text-orange-600" />
            <span className="text-xs font-medium text-orange-700">
              {pendingDeletions.length} sale{pendingDeletions.length > 1 ? "s" : ""} awaiting deletion
            </span>
          </button>
        )}

        {/* Pending Return Approvals Badge — master/super_user only */}
        {pendingReturnApprovals.length > 0 && (user?.role === "master" || user?.role === "super_user") && (
          <button
            type="button"
            onClick={() => setPendingReturnApprovalsOpen(true)}
            className="flex items-center gap-2 px-3 py-1 rounded-md bg-amber-50 border border-amber-200 hover:bg-amber-100 hover:border-amber-300 transition-colors cursor-pointer"
            data-testid="pending-return-approvals-badge"
            title="Click to approve/reject return requests"
          >
            <Clock className="w-4 h-4 text-amber-600" />
            <span className="text-xs font-medium text-amber-700">
              {pendingReturnApprovals.length} return{pendingReturnApprovals.length > 1 ? "s" : ""} pending approval
            </span>
          </button>
        )}

        <div className="text-xs text-muted-foreground sm:ml-auto">
          {/* Exclude cancelled sales from count and total - they appear in list but don't affect financial totals */}
          {(() => {
            const activeSales = items.filter(s => s.status !== "canceled");
            const count = activeSales.length;
            const total = activeSales.reduce((acc, sale) => {
              // When "all" filter: sum full sale totals
              if (paymentModeFilter === "all") {
                return acc + Number(sale.total || 0);
              }
              // When specific mode filter: sum only that mode's payment amount
              const modePayments = (sale.payments || [])
                .filter(p => (p.mode || "").toLowerCase() === paymentModeFilter)
                .reduce((sum, p) => sum + Number(p.amount || 0), 0);
              return acc + modePayments;
            }, 0);
            return `${count} sale${count === 1 ? "" : "s"} · ₹${total.toFixed(2)}`;
          })()}
        </div>
        <Button
          onClick={handleNewSaleClick}
          style={{ backgroundColor: "#9E6D5B" }}
          data-testid="new-sale-btn"
          className="w-full sm:w-auto"
        >
          <Plus className="w-4 h-4 mr-2" /> New sale
        </Button>
      </div>

      <Card className="overflow-hidden" data-testid="sales-table-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--muted))]">
              <tr className="text-left">
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">When</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Customer</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Items/Qty</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Total</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Payment</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">By</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((s) => {
                const canceled = s.status === "canceled";
                const adjusted = (s.adjustments || []).length > 0;
                const reviewNeeded = !!s.review_required;
                return (
                  <tr
                    key={s.id}
                    className={`border-t border-[hsl(var(--border))] ${
                      reviewNeeded ? "bg-amber-50/70" : ""
                    } ${canceled ? "opacity-60" : ""}`}
                    data-testid={`sale-row-${s.id}`}
                  >
                    <td className="p-3 text-xs font-mono-data text-muted-foreground">
                      {formatDateTime(s.created_at)}
                      {reviewNeeded && (
                        <div
                          className="inline-flex items-center gap-1 ml-2 text-[10px] uppercase tracking-widest text-[#B4602A] font-semibold"
                          data-testid={`sale-review-flag-${s.id}`}
                        >
                          <AlertTriangle className="w-3 h-3" /> Review
                        </div>
                      )}
                      {canceled && <span className="pill pill-urgent ml-1">canceled</span>}
                      {adjusted && !canceled && <span className="pill pill-high ml-1">edited</span>}
                    </td>
                    <td className="p-3">
                      {s.customer_id ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/customers/${s.customer_id}`);
                          }}
                          className="text-left hover:text-[#9E6D5B] hover:underline transition-colors cursor-pointer font-medium"
                          data-testid={`sale-customer-link-${s.id}`}
                        >
                          {s.customer_name || "—"}
                        </button>
                      ) : (
                        <span>{s.customer_name || "—"}</span>
                      )}
                    </td>
                    <td className="p-3 text-right font-mono-data">
                      {/* Items count with quantity, and returned qty if any */}
                      <div>{s.line_items?.length || 0} items</div>
                      <div className="text-[10px] text-muted-foreground">
                        {s.line_items?.reduce((sum, li) => sum + (li.quantity || 0), 0) || 0} qty
                      </div>
                      {/* Manual items indicator - items without barcode or AUTO code */}
                      {(() => {
                        const manualQty = (s.line_items || []).reduce((sum, li) => {
                          const autoBarcode = li.product_snapshot?.auto_barcode || "";
                          const manualBarcode = li.product_snapshot?.manual_barcode || "";
                          if (!autoBarcode && !manualBarcode) {
                            return sum + (li.quantity || 1);
                          }
                          return sum;
                        }, 0);
                        return manualQty > 0 ? (
                          <div 
                            className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium bg-orange-50 text-orange-700 border border-orange-200"
                            data-testid={`sale-manual-qty-${s.id}`}
                            title="Manually added items without barcode"
                          >
                            {manualQty} manual
                          </div>
                        ) : null;
                      })()}
                      {s.returns && s.returns.length > 0 && (
                        <div className="text-[10px] text-rose-600">
                          returned: {s.returns.reduce((sum, r) => sum + (r.quantity || 0), 0)} qty
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-right font-mono-data font-semibold">
                      {/* Show ₹0 for cancelled sales */}
                      {s.status === "canceled" ? (
                        <span className="text-muted-foreground line-through">₹{Number(s.total || 0).toFixed(2)}</span>
                      ) : (
                        <>
                          ₹{Number(s.total || 0).toFixed(2)}
                          {/* Show refund from either refund_total or total_returned_amount */}
                          {(Number(s.refund_total || 0) > 0 || Number(s.total_returned_amount || 0) > 0) && (
                            <div className="text-[10px] text-rose-700">
                              refund ₹{Number(s.refund_total || 0) + Number(s.total_returned_amount || 0)}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="p-3">
                      {/* Show cancellation reason for cancelled sales */}
                      {s.status === "canceled" ? (
                        <span className="text-xs text-muted-foreground italic">
                          {s.canceled_reason || "No reason provided"}
                        </span>
                      ) : (
                        <>
                          {/* Show partial payments breakdown if available */}
                          {s.payments && s.payments.length > 0 ? (
                            <div className="text-xs space-y-0.5">
                              {s.payments.map((p) => (
                                <div key={`${p.mode}-${p.amount}`} className="font-mono-data">
                                  <span className="uppercase text-muted-foreground">{p.mode}</span>: ₹{Number(p.amount || 0).toFixed(2)}
                                </div>
                              ))}
                              {/* Show dues collected if any */}
                              {s.dues_collected && s.dues_collected.amount > 0 && (
                                <div className="font-mono-data text-orange-600 border-t border-dashed pt-0.5 mt-0.5">
                                  <span className="uppercase">+DUES</span>: ₹{Number(s.dues_collected.amount).toFixed(2)}
                                  <span className="text-[9px] ml-1">({s.dues_collected.mode})</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs font-mono-data uppercase mr-2">{s.payment_mode}</span>
                          )}
                          <span
                            className={`pill mt-1 inline-block ${
                              s.payment_status === "paid"
                                ? "pill-normal"
                                : s.payment_status === "partial"
                                  ? "pill-high"
                                  : "pill-urgent"
                            }`}
                          >
                            {s.payment_status}
                          </span>
                        </>
                      )}
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">{s.created_by_name || "—"}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setViewTarget(s)}
                        title="View details"
                        data-testid={`sale-view-btn-${s.id}`}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" />
                        View
                      </Button>
                      {reviewNeeded && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            try {
                              await api.post(`/sales/${s.id}/review`);
                              toast.success("Marked as reviewed");
                              load();
                            } catch (e) {
                              toast.error(formatApiError(e));
                            }
                          }}
                          title="Mark reviewed"
                          className="text-emerald-700"
                          data-testid={`sale-review-btn-${s.id}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!items.length && !loading && (
                <tr>
                  <td colSpan={8} className="p-10 text-center text-sm text-muted-foreground">
                    No sales yet. Hit "New sale" to record the first one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Multiple New Sale tabs */}
      {openSaleTabs.map((tab) => (
        <NewSaleDialog
          key={tab.id}
          open={true}
          onOpenChange={(v) => { if (!v) closeSaleTab(tab.id); }}
          shop={tab.shop}
          onDone={() => {
            closeSaleTab(tab.id);
            load();
          }}
        />
      ))}
      
      {/* Edit Sale (separate from multi-tab) */}
      <NewSaleDialog
        open={!!editTarget}
        onOpenChange={(v) => { if (!v) setEditTarget(null); }}
        shop={editTarget ? { id: editTarget.shop_id, name: editTarget.shop_name } : shop}
        onDone={load}
        editSale={editTarget}
        editReason=""
      />
      <CancelSaleDialog
        sale={cancelTarget}
        onOpenChange={(v) => { if (!v) setCancelTarget(null); }}
        onDone={load}
      />
      <PendingDeletionsDialog
        open={pendingDeletionsOpen}
        onOpenChange={setPendingDeletionsOpen}
        onChange={() => {
          loadPendingDeletions();
          load();
        }}
      />
      <SalesReturnDialog
        open={!!returnTarget}
        onOpenChange={(v) => { if (!v) setReturnTarget(null); }}
        saleId={returnTarget?.id}
        onSuccess={() => {
          setReturnTarget(null);
          load();
        }}
      />
      <PendingReturnApprovalsDialog
        open={pendingReturnApprovalsOpen}
        onOpenChange={setPendingReturnApprovalsOpen}
        onSuccess={() => {
          loadPendingReturnApprovals();
          load();
        }}
      />
      <SaleDetailDialog
        sale={viewTarget}
        onOpenChange={(v) => { if (!v) setViewTarget(null); }}
        onEdit={(s) => setEditTarget(s)}
        onReturn={(s) => setReturnTarget(s)}
        onCancel={(s) => setCancelTarget(s)}
      />
      <SelectShopForSaleDialog
        open={shopSelectOpen}
        onOpenChange={setShopSelectOpen}
        shops={accessibleShops}
        onSelectShop={(selectedShop) => openNewSaleTab(selectedShop)}
      />
    </div>
  );
}

function LedgerTab({ filter, kind, title, endpoint, tone }) {
  const { shop, shopId, isAllShops, dateFrom, dateTo } = filter;
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ total: 0, count: 0 });
  const [form, setForm] = useState({
    date: istToday(),
    category: "",
    amount: "",
    payment_mode: "cash",
    note: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (shopId) params.shop_id = shopId;
      const { data } = await api.get(endpoint, { params });
      setItems(data.items || []);
      setTotals({ total: data.total || 0, count: data.count || 0 });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, dateFrom, dateTo, isAllShops]);

  const submit = async (e) => {
    e.preventDefault();
    if (!shop) return;
    if (!form.category.trim()) return toast.error("Category is required");
    if (!Number(form.amount)) return toast.error("Amount is required");
    setSaving(true);
    try {
      await api.post(endpoint, {
        shop_id: shop.id,
        date: form.date,
        category: form.category.trim(),
        amount: Number(form.amount),
        payment_mode: form.payment_mode,
        note: form.note,
      });
      toast.success(`${title} added`);
      setForm({
        date: istToday(),
        category: "",
        amount: "",
        payment_mode: "cash",
        note: "",
      });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this entry?")) return;
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const accent = tone === "emerald" ? "#2D6A4F" : "#9A3412";

  return (
    <div className="grid md:grid-cols-3 gap-4">
      <Card className="p-5 md:col-span-1" data-testid={`ledger-form-${kind}`}>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
          Add {title.toLowerCase()}
        </div>
        {isAllShops ? (
          <div className="text-sm text-muted-foreground py-4" data-testid={`ledger-${kind}-all-shops-hint`}>
            Pick a specific shop above to add entries. The list on the right is a combined view.
          </div>
        ) : (
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Date *</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="mt-1"
              required
              data-testid={`ledger-${kind}-date`}
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Category *</Label>
            <Input
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder={kind === "expenses" ? "e.g. Rent, Power bill" : "e.g. Rebate, Gift card"}
              className="mt-1"
              required
              data-testid={`ledger-${kind}-category`}
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Amount ₹ *</Label>
            <Input
              type="number"
              step="0.01"
              min={0}
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="mt-1 font-mono-data"
              required
              data-testid={`ledger-${kind}-amount`}
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Mode</Label>
            <Select
              value={form.payment_mode}
              onValueChange={(v) => setForm({ ...form, payment_mode: v })}
            >
              <SelectTrigger className="mt-1" data-testid={`ledger-${kind}-mode`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAY_MODES.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Note</Label>
            <Textarea
              rows={2}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="mt-1"
              data-testid={`ledger-${kind}-note`}
            />
          </div>
          <Button
            type="submit"
            disabled={saving}
            style={{ backgroundColor: accent }}
            className="w-full"
            data-testid={`ledger-${kind}-submit`}
          >
            {saving ? "Saving…" : `Add ${title.toLowerCase()}`}
          </Button>
        </form>
        )}
      </Card>

      <Card className="p-0 md:col-span-2 overflow-hidden" data-testid={`ledger-list-${kind}`}>
        <div className="p-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">
            Recent · {totals.count} entries
          </div>
          <div className="font-mono-data font-semibold text-lg" style={{ color: accent }}>
            ₹{Number(totals.total || 0).toFixed(2)}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--muted))]">
              <tr className="text-left">
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Date</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Category</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Amount</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Mode</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Note</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-t border-[hsl(var(--border))]" data-testid={`ledger-row-${e.id}`}>
                  <td className="p-3 text-xs font-mono-data">{formatDate(e.date)}</td>
                  <td className="p-3 font-medium">{e.category}</td>
                  <td className="p-3 text-right font-mono-data">₹{Number(e.amount || 0).toFixed(2)}</td>
                  <td className="p-3 uppercase text-xs font-mono-data">{e.payment_mode}</td>
                  <td className="p-3 text-xs text-muted-foreground truncate max-w-[220px]" title={e.note}>{e.note || "—"}</td>
                  <td className="p-3 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove(e.id)}
                      data-testid={`ledger-delete-${e.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
              {!items.length && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">
                    No entries yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}


const DENOM_DEFAULTS = [
  { value: 500, label: "₹500" },
  { value: 200, label: "₹200" },
  { value: 100, label: "₹100" },
  { value: 50, label: "₹50" },
  { value: 20, label: "₹20" },
  { value: 10, label: "₹10" },
  { value: 5, label: "₹5" },
  { value: 2, label: "₹2" },
  { value: 1, label: "₹1" },
];

function CashRegisterTab({ shop, filter }) {
  // Use date from parent filter for synchronization with Drawer tab
  // Only works with single-day selection (dateFrom === dateTo)
  const filterDate = filter?.dateFrom;
  const isSingleDay = filter?.dateFrom && filter?.dateTo && filter.dateFrom === filter.dateTo;
  
  const [day, setDay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingOpen, setSavingOpen] = useState(false);
  const [savingClose, setSavingClose] = useState(false);
  const [openingInput, setOpeningInput] = useState("");
  // counts[value] = number
  const [counts, setCounts] = useState({});
  // History
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const load = async () => {
    if (!shop?.id || !filterDate) return;
    setLoading(true);
    try {
      const { data } = await api.get("/cash-register/day", { params: { shop_id: shop.id, date: filterDate } });
      setDay(data);
      setOpeningInput(String(Number(data.opening_amount || 0).toFixed(2)));
      const c = {};
      for (const d of data.denominations || []) {
        c[String(d.value)] = Number(d.count || 0);
      }
      setCounts(c);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    if (!shop?.id) return;
    try {
      const { data } = await api.get("/cash-register/history", { params: { shop_id: shop.id, limit: 30 } });
      setHistory(data.items || []);
    } catch {
      setHistory([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop?.id, filterDate]);

  useEffect(() => {
    if (showHistory) loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHistory, shop?.id]);

  const closingAmount = useMemo(() => {
    return DENOM_DEFAULTS.reduce(
      (a, d) => a + Number(d.value) * Number(counts[String(d.value)] || 0),
      0,
    );
  }, [counts]);

  const opening = Number(day?.opening_amount || 0);
  const salesCash = Number(day?.sales_cash || 0);
  const incomeCash = Number(day?.income_cash || 0);
  const expenseCash = Number(day?.expense_cash || 0);
  const expected = opening + salesCash + incomeCash - expenseCash;
  const variance = Number((closingAmount - expected).toFixed(2));
  const varianceAbs = Math.abs(variance);
  const persistedClose = day?.closing_amount;

  const saveOpening = async () => {
    if (!shop?.id || !filterDate) return;
    const amt = Number(openingInput || 0);
    if (amt < 0) return toast.error("Opening cannot be negative");
    setSavingOpen(true);
    try {
      const { data } = await api.post("/cash-register/opening", {
        shop_id: shop.id,
        date: filterDate,
        amount: amt,
      });
      setDay(data);
      toast.success("Opening cash saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingOpen(false);
    }
  };

  const saveClosing = async () => {
    if (!shop?.id || !filterDate) return;
    setSavingClose(true);
    try {
      const denominations = DENOM_DEFAULTS
        .map((d) => ({ value: d.value, count: Number(counts[String(d.value)] || 0) }))
        .filter((d) => d.count > 0);
      const { data } = await api.post("/cash-register/closing", {
        shop_id: shop.id,
        date: filterDate,
        denominations,
      });
      setDay(data);
      toast.success("Closing cash recorded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingClose(false);
    }
  };

  if (!shop) {
    return (
      <Card className="p-10 text-center text-sm text-muted-foreground">
        Pick a shop to manage its cash register.
      </Card>
    );
  }

  // Show guidance when date range spans multiple days
  if (!isSingleDay) {
    return (
      <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="cash-needs-single-day">
        <Clock className="w-10 h-10 mx-auto mb-3 opacity-30" />
        <div className="font-medium mb-1">Select a Single Day</div>
        Cash register works on a single day at a time. Click <span className="font-semibold">Today</span> or <span className="font-semibold">Yesterday</span> above.
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="cash-register-tab">
      <Card className="p-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Shop</Label>
          <div className="font-heading text-base mt-1">{shop.name}</div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Date</Label>
          <div className="font-heading text-base mt-1" data-testid="cash-date-display">
            {new Date(filterDate).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
          </div>
          <div className="text-xs text-muted-foreground">Use Today/Yesterday buttons above to change</div>
        </div>
        {loading && <span className="text-xs text-muted-foreground">Loading…</span>}
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Opening */}
        <Card className="p-5" data-testid="cash-opening-card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Opening cash</div>
            {day?.opening_source === "prev_close" && day?.opening_from_date && (
              <span className="text-[10px] text-emerald-700 font-mono-data">
                from {day.opening_from_date} closing
              </span>
            )}
            {day?.opening_source === "manual" && (
              <span className="text-[10px] text-muted-foreground font-mono-data">manual</span>
            )}
          </div>
          <div className="flex gap-2">
            <Input
              type="number"
              step="0.01"
              min={0}
              value={openingInput}
              onChange={(e) => setOpeningInput(e.target.value)}
              className="font-mono-data text-lg h-11"
              data-testid="cash-opening-input"
            />
            <Button
              onClick={saveOpening}
              disabled={savingOpen}
              style={{ backgroundColor: "#9E6D5B" }}
              data-testid="cash-opening-save"
            >
              {savingOpen ? "Saving…" : "Save"}
            </Button>
          </div>
          <div className="text-xs text-muted-foreground mt-2">
            Should equal yesterday's physical closing. Override if you added/removed cash from the till manually.
          </div>

          <div className="mt-5 border-t border-[hsl(var(--border))] pt-4 space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span>Opening</span>
              <span className="font-mono-data">₹{opening.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-emerald-700">
              <span>+ Cash sales</span>
              <span className="font-mono-data">₹{salesCash.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-emerald-700">
              <span>+ Cash income</span>
              <span className="font-mono-data">₹{incomeCash.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-rose-700">
              <span>− Cash expenses</span>
              <span className="font-mono-data">₹{expenseCash.toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t border-[hsl(var(--border))] pt-2 font-semibold">
              <span>Expected closing</span>
              <span className="font-mono-data" data-testid="cash-expected">₹{expected.toFixed(2)}</span>
            </div>
          </div>
        </Card>

        {/* Closing denomination table */}
        <Card className="p-5" data-testid="cash-closing-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Closing cash (physical count)</div>
              <div className="text-xs text-muted-foreground mt-0.5">Enter the number of notes/coins at shop close.</div>
            </div>
          </div>
          <div className="overflow-hidden rounded-md border border-[hsl(var(--border))]">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--muted))]">
                <tr className="text-left">
                  <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground font-medium">Denom.</th>
                  <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Count</th>
                  <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {DENOM_DEFAULTS.map((d) => {
                  const n = Number(counts[String(d.value)] || 0);
                  return (
                    <tr key={d.value} className="border-t border-[hsl(var(--border))]" data-testid={`cash-denom-row-${d.value}`}>
                      <td className="p-2 font-mono-data">{d.label}</td>
                      <td className="p-2 text-right">
                        <Input
                          type="number"
                          min={0}
                          value={counts[String(d.value)] ?? ""}
                          onChange={(e) =>
                            setCounts((c) => ({ ...c, [String(d.value)]: Math.max(0, parseInt(e.target.value || "0") || 0) }))
                          }
                          className="h-8 w-20 ml-auto text-right font-mono-data"
                          data-testid={`cash-denom-input-${d.value}`}
                        />
                      </td>
                      <td className="p-2 text-right font-mono-data">₹{(n * d.value).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-[hsl(var(--muted))]">
                  <td colSpan={2} className="p-2 text-right font-semibold">Closing total</td>
                  <td className="p-2 text-right font-mono-data font-semibold" data-testid="cash-closing-total">
                    ₹{closingAmount.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className={`mt-4 p-3 rounded-md text-sm ${
            varianceAbs < 0.01
              ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
              : "bg-amber-50 text-[#B4602A] border border-amber-200"
          }`} data-testid="cash-variance">
            <div className="flex justify-between">
              <span>Variance</span>
              <span className="font-mono-data font-semibold">
                {variance > 0 ? "+" : ""}₹{variance.toFixed(2)}
              </span>
            </div>
            <div className="text-xs mt-1 opacity-80">
              {varianceAbs < 0.01
                ? "Matches expected to the paise."
                : variance > 0
                  ? "You counted more than expected — check for miskeyed sales or cash inflows."
                  : "You counted less than expected — check for missed expenses or change discrepancies."}
            </div>
          </div>

          <div className="mt-3 flex justify-end">
            <Button
              onClick={saveClosing}
              disabled={savingClose}
              style={{ backgroundColor: "#2D6A4F" }}
              data-testid="cash-closing-save"
            >
              {savingClose ? "Saving…" : persistedClose != null ? "Update closing" : "Record closing"}
            </Button>
          </div>

          {persistedClose != null && day?.closed_at && (
            <div className="text-[11px] text-muted-foreground mt-2">
              Last closed {formatDateTime(day.closed_at)}
              {day.closed_by_name ? ` · by ${day.closed_by_name}` : ""}
            </div>
          )}
        </Card>
      </div>

      {/* History Toggle */}
      <Card className="p-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowHistory(!showHistory)}
          className="w-full"
          data-testid="cash-history-toggle"
        >
          {showHistory ? "Hide History" : "Show Cash Register History"}
        </Button>
        
        {showHistory && (
          <div className="mt-4 overflow-x-auto">
            {history.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-6">
                No cash register history found for this shop.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--muted))]">
                  <tr className="text-left">
                    <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground">Date</th>
                    <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground text-right">Opening</th>
                    <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground text-right">Sales Cash</th>
                    <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground text-right">Income</th>
                    <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground text-right">Expense</th>
                    <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground text-right">Expected</th>
                    <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground text-right">Closing</th>
                    <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground text-right">Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => {
                    const open = Number(h.opening_amount || 0);
                    const salesCash = Number(h.sales_cash || 0);
                    const income = Number(h.income_cash || 0);
                    const expense = Number(h.expense_cash || 0);
                    const expected = open + salesCash + income - expense;
                    const closing = h.closing_amount != null ? Number(h.closing_amount) : null;
                    const variance = closing != null ? Number((closing - expected).toFixed(2)) : null;
                    
                    return (
                      <tr 
                        key={h.id || h.date} 
                        className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/50"
                        data-testid={`cash-history-row-${h.date}`}
                      >
                        <td className="p-2 font-medium">{formatDate(h.date)}</td>
                        <td className="p-2 text-right font-mono-data">₹{open.toFixed(2)}</td>
                        <td className="p-2 text-right font-mono-data text-green-700">₹{salesCash.toFixed(2)}</td>
                        <td className="p-2 text-right font-mono-data text-blue-700">₹{income.toFixed(2)}</td>
                        <td className="p-2 text-right font-mono-data text-red-700">₹{expense.toFixed(2)}</td>
                        <td className="p-2 text-right font-mono-data">₹{expected.toFixed(2)}</td>
                        <td className="p-2 text-right font-mono-data">
                          {closing != null ? `₹${closing.toFixed(2)}` : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className={`p-2 text-right font-mono-data font-semibold ${
                          variance == null 
                            ? "text-muted-foreground" 
                            : Math.abs(variance) < 0.01 
                              ? "text-emerald-600" 
                              : variance > 0 
                                ? "text-amber-600" 
                                : "text-red-600"
                        }`}>
                          {variance != null ? (
                            `${variance >= 0 ? "+" : ""}₹${variance.toFixed(2)}`
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}


function BalanceTab() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/credit/pending", { params: { limit: 500 } });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filteredItems = useMemo(() => {
    if (!data?.items) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return data.items;
    return data.items.filter((i) =>
      (i.name || "").toLowerCase().includes(needle) ||
      (i.phone1 || "").includes(needle) ||
      (i.place || "").toLowerCase().includes(needle)
    );
  }, [data, q]);

  const customersOwe = filteredItems.filter((c) => c.balance_status === "customer_owes");
  const shopOwes = filteredItems.filter((c) => c.balance_status === "shop_owes");

  return (
    <div className="space-y-4" data-testid="balance-tab">
      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="customer-balance-summary">
        <Card className="p-4 border-orange-200 bg-orange-50/50">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-orange-600" />
            <span className="text-[10px] uppercase tracking-widest text-orange-700">Receivable</span>
          </div>
          <div className="text-2xl font-mono-data font-bold text-orange-700 mt-2" data-testid="balance-total-receivable">
            ₹{Number(data?.total_receivable || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </div>
          <div className="text-[11px] text-orange-700/80 mt-1">
            {data?.customers_owe_count || 0} customer{(data?.customers_owe_count !== 1) ? "s" : ""} owe you
          </div>
        </Card>
        <Card className="p-4 border-emerald-200 bg-emerald-50/50">
          <div className="flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-emerald-600" />
            <span className="text-[10px] uppercase tracking-widest text-emerald-700">Payable</span>
          </div>
          <div className="text-2xl font-mono-data font-bold text-emerald-700 mt-2" data-testid="balance-total-payable">
            ₹{Number(data?.total_payable || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </div>
          <div className="text-[11px] text-emerald-700/80 mt-1">
            {data?.shop_owes_count || 0} customer{(data?.shop_owes_count !== 1) ? "s" : ""} have credit
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2">
            <Scale className="w-4 h-4 text-[#9E6D5B]" />
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Net</span>
          </div>
          <div
            className={`text-2xl font-mono-data font-bold mt-2 ${(data?.net || 0) > 0 ? "text-orange-700" : (data?.net || 0) < 0 ? "text-emerald-700" : "text-muted-foreground"}`}
            data-testid="balance-net"
          >
            ₹{Math.abs(Number(data?.net || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            {(data?.net || 0) > 0 ? "Net receivable" : (data?.net || 0) < 0 ? "Net payable" : "Balanced"}
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-4 h-4 text-muted-foreground" />
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Accounts</span>
          </div>
          <div className="text-2xl font-mono-data font-bold mt-2">{data?.total || 0}</div>
          <div className="text-[11px] text-muted-foreground mt-1">With pending balance</div>
        </Card>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-9 h-10 bg-white"
            placeholder="Search by name, phone, place…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="balance-search"
          />
        </div>
        <Button variant="outline" onClick={load} disabled={loading} title="Refresh" data-testid="balance-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Side-by-side layout for desktop, stacked on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Customers Who Owe You (Left/Top) */}
        <div className="min-h-[200px]">
          <h3 className="text-xs font-semibold text-orange-700 mb-2 flex items-center gap-2 uppercase tracking-widest sticky top-0 bg-[hsl(var(--background))] py-2 z-10">
            <TrendingUp className="w-3.5 h-3.5" />
            Customers Who Owe You ({customersOwe.length})
          </h3>
          {customersOwe.length > 0 ? (
            <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
              {customersOwe.map((c) => (
                <BalanceCustomerRow
                  key={c.customer_id}
                  customer={c}
                  onClick={() => navigate(`/customers/${c.customer_id}`)}
                />
              ))}
            </div>
          ) : (
            <Card className="p-6 text-center text-muted-foreground">
              <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No outstanding receivables 🎉</p>
            </Card>
          )}
        </div>

        {/* Customers With Store Credit (Right/Bottom) */}
        <div className="min-h-[200px]">
          <h3 className="text-xs font-semibold text-emerald-700 mb-2 flex items-center gap-2 uppercase tracking-widest sticky top-0 bg-[hsl(var(--background))] py-2 z-10">
            <TrendingDown className="w-3.5 h-3.5" />
            Customers With Store Credit ({shopOwes.length})
          </h3>
          {shopOwes.length > 0 ? (
            <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
              {shopOwes.map((c) => (
                <BalanceCustomerRow
                  key={c.customer_id}
                  customer={c}
                  onClick={() => navigate(`/customers/${c.customer_id}`)}
                />
              ))}
            </div>
          ) : (
            <Card className="p-6 text-center text-muted-foreground">
              <TrendingDown className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No store credits outstanding</p>
            </Card>
          )}
        </div>
      </div>

      {!loading && filteredItems.length === 0 && (
        <Card className="p-10 text-center text-muted-foreground" data-testid="balance-empty">
          <Scale className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">{q ? "No customers match your search" : "All settled — no pending balances 🎉"}</p>
        </Card>
      )}
    </div>
  );
}

function BalanceCustomerRow({ customer, onClick }) {
  const isOwing = customer.balance_status === "customer_owes";
  const sc = Number(customer.store_credit || 0);
  return (
    <Card
      onClick={onClick}
      className={`p-3 cursor-pointer transition-all hover:shadow-md ${isOwing ? "border-orange-200 hover:border-orange-300" : "border-emerald-200 hover:border-emerald-300"}`}
      data-testid={`balance-row-${customer.customer_id}`}
    >
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{customer.name}</span>
            {customer.place && (
              <span className="text-[11px] text-muted-foreground flex items-center gap-1 shrink-0">
                <MapPin className="w-3 h-3" />{customer.place}
              </span>
            )}
            {sc < -0.01 && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 shrink-0">
                Over-paid
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-[11px] text-muted-foreground">
            {customer.phone1 && (
              <span className="flex items-center gap-1 font-mono-data">
                <Phone className="w-3 h-3" />{customer.phone1}
              </span>
            )}
            {customer.sale_count > 0 && <span>{customer.sale_count} purchase{customer.sale_count > 1 ? "s" : ""}</span>}
            {customer.last_sale_at && <span>Last: {formatDate(customer.last_sale_at)}</span>}
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {customer.receivable > 0 && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-orange-600">Receivable</div>
              <div className="text-sm font-mono-data font-semibold text-orange-700">
                ₹{Number(customer.receivable).toFixed(2)}
              </div>
            </div>
          )}
          {sc > 0 && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-emerald-600">Store Credit</div>
              <div className="text-sm font-mono-data font-semibold text-emerald-700">
                ₹{sc.toFixed(2)}
              </div>
            </div>
          )}
          {sc < -0.01 && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-amber-700">Over-Paid</div>
              <div className="text-sm font-mono-data font-semibold text-amber-800">
                ₹{Math.abs(sc).toFixed(2)}
              </div>
            </div>
          )}
          <div className="text-right border-l pl-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Net</div>
            <div className={`text-base font-mono-data font-bold ${isOwing ? "text-orange-700" : "text-emerald-700"}`}>
              ₹{Math.abs(customer.net_balance).toFixed(2)}
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground" />
        </div>
      </div>
    </Card>
  );
}
